import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Connection state management
let connectionListeners = [];
let isConnected = true;
let isRetrying = false;

export const getConnectionState = () => ({ isConnected, isRetrying });

export const subscribeToConnection = (callback) => {
  connectionListeners.push(callback);
  return () => {
    connectionListeners = connectionListeners.filter(cb => cb !== callback);
  };
};

const notifyListeners = () => {
  connectionListeners.forEach(cb => cb({ isConnected, isRetrying }));
};

/**
 * Robust API fetch with extended retry logic for cold-start backends
 * - Longer initial timeout (20s) for cold starts
 * - Up to 5 retries with exponential backoff
 * - Visual feedback through connection state
 */
export const fetchWithRetry = async (url, options = {}, config = {}) => {
  const {
    retries = 5,
    initialTimeout = 20000, // 20 seconds for cold starts
    maxTimeout = 45000,     // 45 seconds max
    backoffMultiplier = 1.5
  } = config;

  let lastError;
  
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      // Increase timeout with each retry
      const timeout = Math.min(initialTimeout * Math.pow(backoffMultiplier, attempt), maxTimeout);
      
      if (attempt > 0) {
        isRetrying = true;
        notifyListeners();
      }
      
      const response = await axios({
        url,
        timeout,
        withCredentials: true,
        ...options
      });
      
      // Success - reset connection state
      if (!isConnected || isRetrying) {
        isConnected = true;
        isRetrying = false;
        notifyListeners();
      }
      
      return response;
    } catch (error) {
      lastError = error;
      
      // Don't retry on 4xx errors (client errors)
      if (error.response && error.response.status >= 400 && error.response.status < 500) {
        throw error;
      }
      
      // Calculate delay with exponential backoff
      const delay = Math.min(1000 * Math.pow(2, attempt), 8000);
      
      if (attempt < retries - 1) {
        console.log(`API retry ${attempt + 1}/${retries} for ${url}, waiting ${delay}ms...`);
        await new Promise(r => setTimeout(r, delay));
      }
    }
  }
  
  // All retries failed
  isConnected = false;
  isRetrying = false;
  notifyListeners();
  
  throw lastError;
};

/**
 * Quick health check to wake up the backend
 */
export const wakeBackend = async () => {
  isRetrying = true;
  notifyListeners();
  
  try {
    // Simple health check endpoint
    await axios.get(`${API_BASE}/health`, { timeout: 30000 });
    isConnected = true;
    isRetrying = false;
    notifyListeners();
    return true;
  } catch (error) {
    // Try track-record as fallback (always exists)
    try {
      await axios.get(`${API_BASE}/track-record`, { timeout: 30000 });
      isConnected = true;
      isRetrying = false;
      notifyListeners();
      return true;
    } catch (e) {
      isConnected = false;
      isRetrying = false;
      notifyListeners();
      return false;
    }
  }
};

export default {
  fetchWithRetry,
  wakeBackend,
  getConnectionState,
  subscribeToConnection,
  API_BASE
};
